/**
 * FleetPanel Component
 * 
 * Main hub for fleet (guild/corporation) management.
 * Tabs: Overview, Members, Treasury, Territories, Activity, Settings
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { useConfirm } from '../contexts/ConfirmContext';
import {
  Fleet,
  FleetMember,
  FleetUpgrade,
  FleetInvitation,
  FleetApplication,
  FleetActivityLog,
  FleetRank,
  FleetUpgradeType,
  RANK_DISPLAY,
  UPGRADE_DISPLAY,
  UPGRADE_COSTS,
  FLEET_CREATION_COST,
  FLEET_CREATION_MIN_LEVEL,
} from '../types/fleet';
import { formatDisplayName } from '../utils/formatDisplayName';

// ============ TYPES ============

interface FleetPanelProps {
  isOpen: boolean;
  onClose: () => void;
  socket: Socket | null;
  playerBerries: number;
  playerLevel: number;
  playerName: string;
  characterId: string;
  isDockedAtHarborOffice: boolean;
  harborOfficeName?: string;
}

type TabType = 'overview' | 'members' | 'treasury' | 'activity' | 'settings';

// ============ STYLES ============

const panelStyle: React.CSSProperties = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '700px',
  maxWidth: '95vw',
  maxHeight: '85vh',
  backgroundColor: '#0d1117',
  border: '1px solid #21262d',
  zIndex: 5001,
  display: 'flex',
  flexDirection: 'column',
  fontFamily: '"Sometype Mono", monospace',
  boxShadow: '0 0 0 1px #161b22, 0 20px 50px rgba(0, 0, 0, 0.8)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 16px',
  borderBottom: '1px solid #21262d',
  background: 'linear-gradient(180deg, #161b22 0%, #0d1117 100%)',
};

const tabsStyle: React.CSSProperties = {
  display: 'flex',
  borderBottom: '1px solid #21262d',
  backgroundColor: '#161b22',
};

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: '10px 12px',
  cursor: 'pointer',
  backgroundColor: active ? 'rgba(255, 255, 255, 0.06)' : 'transparent',
  color: active ? '#e6edf3' : '#8b949e',
  border: 'none',
  borderBottom: active ? '2px solid #58a6ff' : '2px solid transparent',
  fontFamily: '"Sometype Mono", monospace',
  fontSize: '12px',
  fontWeight: active ? 'bold' : 'normal',
  transition: 'all 0.15s ease',
});

const contentStyle: React.CSSProperties = {
  flex: 1,
  overflow: 'auto',
  padding: '16px',
};

const buttonStyle = (variant: 'primary' | 'secondary' | 'danger' = 'primary'): React.CSSProperties => {
  const colors: Record<string, { bg: string; hover: string }> = {
    primary: { bg: '#238636', hover: '#2ea043' },
    secondary: { bg: '#21262d', hover: '#30363d' },
    danger: { bg: '#da3633', hover: '#f85149' },
  };
  return {
    padding: '8px 16px',
    backgroundColor: colors[variant].bg,
    color: '#e6edf3',
    border: '1px solid #30363d',
    cursor: 'pointer',
    fontFamily: '"Sometype Mono", monospace',
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: 0.5,
  };
};

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  backgroundColor: '#0b0f14',
  border: '1px solid #30363d',
  color: '#e6edf3',
  fontFamily: '"Sometype Mono", monospace',
  fontSize: '12px',
  width: '100%',
};

// ============ COMPONENT ============

export const FleetPanel: React.FC<FleetPanelProps> = ({
  isOpen,
  onClose,
  socket,
  playerBerries,
  playerLevel,
  playerName,
  characterId,
  isDockedAtHarborOffice,
  harborOfficeName,
}) => {
  const { confirm } = useConfirm();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [fleet, setFleet] = useState<Fleet | null>(null);
  const [members, setMembers] = useState<FleetMember[]>([]);
  const [upgrades, setUpgrades] = useState<FleetUpgrade[]>([]);
  const [myRank, setMyRank] = useState<FleetRank | null>(null);
  const [activities, setActivities] = useState<FleetActivityLog[]>([]);
  const [applications, setApplications] = useState<FleetApplication[]>([]);
  const [invitations, setInvitations] = useState<FleetInvitation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Creation form state
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [createName, setCreateName] = useState<string>('');
  const [createTag, setCreateTag] = useState<string>('');
  const [createDescription, setCreateDescription] = useState<string>('');
  const [createJoinPolicy, setCreateJoinPolicy] = useState<'open' | 'invite_only'>('invite_only');

  // Treasury state
  const [depositAmount, setDepositAmount] = useState<string>('');
  const [withdrawAmount, setWithdrawAmount] = useState<string>('');

  // Fetch fleet data
  const fetchFleetData = useCallback((): void => {
    if (!socket) return;
    setIsLoading(true);
    socket.emit('fleet:getMyFleet');
  }, [socket]);

  // Initial fetch
  useEffect(() => {
    if (isOpen && socket) {
      fetchFleetData();
      socket.emit('fleet:getInvitations');
    }
  }, [isOpen, socket, fetchFleetData]);

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;

    const handleMyFleet = (data: {
      fleet: Fleet | null;
      members: FleetMember[];
      upgrades?: FleetUpgrade[];
      myRank: FleetRank | null;
      myMemberId?: string;
    }): void => {
      setFleet(data.fleet);
      setMembers(data.members || []);
      setUpgrades(data.upgrades || []);
      setMyRank(data.myRank);
      setIsLoading(false);
      setShowCreateForm(false);
    };

    const handleError = (data: { message: string }): void => {
      setMessage({ text: data.message, type: 'error' });
      setTimeout(() => setMessage(null), 3000);
    };

    const handleCreated = (data: { fleet: Fleet; cost: number; newBalance: number }): void => {
      setFleet(data.fleet);
      setMyRank('admiral');
      setShowCreateForm(false);
      setMessage({ text: `Fleet "${data.fleet.name}" created for ₱${data.cost.toLocaleString()}!`, type: 'success' });
      setTimeout(() => setMessage(null), 3000);
      fetchFleetData();
    };

    const handleInvitations = (data: { invitations: FleetInvitation[] }): void => {
      setInvitations(data.invitations);
    };

    const handleApplications = (data: { applications: FleetApplication[] }): void => {
      setApplications(data.applications);
    };

    const handleActivityLog = (data: { activities: FleetActivityLog[] }): void => {
      setActivities(data.activities);
    };

    const handleTreasuryUpdated = (data: { treasury: number; change: number; actor: string; type: string }): void => {
      if (fleet) {
        setFleet({ ...fleet, treasury: data.treasury });
      }
    };

    const handleUpgradePurchased = (data: { upgrade: FleetUpgrade; cost: number }): void => {
      setUpgrades(prev => {
        const existing = prev.findIndex(u => u.upgradeType === data.upgrade.upgradeType);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = data.upgrade;
          return updated;
        }
        return [...prev, data.upgrade];
      });
      fetchFleetData();
    };

    const handleMemberJoined = (): void => {
      fetchFleetData();
    };

    const handleMemberLeft = (): void => {
      fetchFleetData();
    };

    const handleDisbanded = (): void => {
      setFleet(null);
      setMembers([]);
      setMyRank(null);
      setMessage({ text: 'Fleet has been disbanded', type: 'error' });
      setTimeout(() => setMessage(null), 3000);
    };

    const handleLeft = (): void => {
      setFleet(null);
      setMembers([]);
      setMyRank(null);
      setMessage({ text: 'You have left the fleet', type: 'success' });
      setTimeout(() => setMessage(null), 3000);
    };

    const handleJoined = (data: { fleet: Fleet }): void => {
      setFleet(data.fleet);
      setMessage({ text: `Joined fleet "${data.fleet.name}"!`, type: 'success' });
      setTimeout(() => setMessage(null), 3000);
      fetchFleetData();
    };

    const handleDepositSuccess = (data: { amount: number; newTreasury: number }): void => {
      if (fleet) {
        setFleet({ ...fleet, treasury: data.newTreasury });
      }
      setDepositAmount('');
      setMessage({ text: `Deposited ₱${data.amount.toLocaleString()}`, type: 'success' });
      setTimeout(() => setMessage(null), 3000);
    };

    const handleWithdrawSuccess = (data: { amount: number; newTreasury: number }): void => {
      if (fleet) {
        setFleet({ ...fleet, treasury: data.newTreasury });
      }
      setWithdrawAmount('');
      setMessage({ text: `Withdrew ₱${data.amount.toLocaleString()}`, type: 'success' });
      setTimeout(() => setMessage(null), 3000);
    };

    socket.on('fleet:myFleet', handleMyFleet);
    socket.on('fleet:error', handleError);
    socket.on('fleet:created', handleCreated);
    socket.on('fleet:invitations', handleInvitations);
    socket.on('fleet:applications', handleApplications);
    socket.on('fleet:activityLog', handleActivityLog);
    socket.on('fleet:treasuryUpdated', handleTreasuryUpdated);
    socket.on('fleet:upgradePurchased', handleUpgradePurchased);
    socket.on('fleet:memberJoined', handleMemberJoined);
    socket.on('fleet:memberLeft', handleMemberLeft);
    socket.on('fleet:disbanded', handleDisbanded);
    socket.on('fleet:left', handleLeft);
    socket.on('fleet:joined', handleJoined);
    socket.on('fleet:depositSuccess', handleDepositSuccess);
    socket.on('fleet:withdrawSuccess', handleWithdrawSuccess);

    return () => {
      socket.off('fleet:myFleet', handleMyFleet);
      socket.off('fleet:error', handleError);
      socket.off('fleet:created', handleCreated);
      socket.off('fleet:invitations', handleInvitations);
      socket.off('fleet:applications', handleApplications);
      socket.off('fleet:activityLog', handleActivityLog);
      socket.off('fleet:treasuryUpdated', handleTreasuryUpdated);
      socket.off('fleet:upgradePurchased', handleUpgradePurchased);
      socket.off('fleet:memberJoined', handleMemberJoined);
      socket.off('fleet:memberLeft', handleMemberLeft);
      socket.off('fleet:disbanded', handleDisbanded);
      socket.off('fleet:left', handleLeft);
      socket.off('fleet:joined', handleJoined);
      socket.off('fleet:depositSuccess', handleDepositSuccess);
      socket.off('fleet:withdrawSuccess', handleWithdrawSuccess);
    };
  }, [socket, fleet, fetchFleetData]);

  // Fetch activity log when tab changes
  useEffect(() => {
    if (activeTab === 'activity' && socket && fleet) {
      socket.emit('fleet:getActivityLog', { limit: 50 });
    }
    if (activeTab === 'settings' && socket && fleet && myRank) {
      socket.emit('fleet:getApplications');
    }
  }, [activeTab, socket, fleet, myRank]);

  // Action handlers
  const handleCreateFleet = (): void => {
    if (!socket) return;
    if (!createName.trim()) {
      setMessage({ text: 'Fleet name is required', type: 'error' });
      return;
    }
    socket.emit('fleet:create', {
      name: createName.trim(),
      tag: createTag.trim() || undefined,
      description: createDescription.trim() || undefined,
      joinPolicy: createJoinPolicy,
    });
  };

  const handleLeaveFleet = (): void => {
    if (!socket || !fleet) return;
    if (myRank === 'admiral') {
      setMessage({ text: 'Admiral must transfer leadership or disband the fleet', type: 'error' });
      return;
    }
    void (async () => {
      const ok: boolean = await confirm({
        title: 'Leave Fleet',
        message: 'Are you sure you want to leave this fleet?',
        confirmText: 'Leave',
        cancelText: 'Cancel',
        tone: 'danger',
      });
      if (!ok) return;
      socket.emit('fleet:leave');
    })();
  };

  const handleDisbandFleet = (): void => {
    if (!socket || !fleet) return;
    void (async () => {
      const ok: boolean = await confirm({
        title: 'Disband Fleet',
        message: `Are you sure you want to disband "${fleet.name}"? This cannot be undone.`,
        confirmText: 'Disband',
        cancelText: 'Cancel',
        tone: 'danger',
      });
      if (!ok) return;
      socket.emit('fleet:disband');
    })();
  };

  const handleDeposit = (): void => {
    if (!socket) return;
    const amount = parseInt(depositAmount, 10);
    if (isNaN(amount) || amount <= 0) {
      setMessage({ text: 'Enter a valid amount', type: 'error' });
      return;
    }
    if (amount > playerBerries) {
      setMessage({ text: 'Insufficient pieces', type: 'error' });
      return;
    }
    socket.emit('fleet:deposit', { amount });
  };

  const handleWithdraw = (): void => {
    if (!socket || !fleet) return;
    const amount = parseInt(withdrawAmount, 10);
    if (isNaN(amount) || amount <= 0) {
      setMessage({ text: 'Enter a valid amount', type: 'error' });
      return;
    }
    if (amount > fleet.treasury) {
      setMessage({ text: 'Insufficient treasury funds', type: 'error' });
      return;
    }
    socket.emit('fleet:withdraw', { amount });
  };

  const handlePurchaseUpgrade = (upgradeType: FleetUpgradeType): void => {
    if (!socket) return;
    socket.emit('fleet:purchaseUpgrade', { upgradeType });
  };

  const handleRespondToInvite = (invitationId: string, accept: boolean): void => {
    if (!socket) return;
    socket.emit('fleet:respondToInvite', { invitationId, accept });
    setInvitations(prev => prev.filter(i => i.id !== invitationId));
  };

  const handleReviewApplication = (applicationId: string, accept: boolean): void => {
    if (!socket) return;
    socket.emit('fleet:reviewApplication', { applicationId, accept });
    setApplications(prev => prev.filter(a => a.id !== applicationId));
  };

  const handleKickMember = (memberId: string, memberName: string): void => {
    if (!socket) return;
    void (async () => {
      const ok: boolean = await confirm({
        title: 'Kick Member',
        message: `Kick ${memberName} from the fleet?`,
        confirmText: 'Kick',
        cancelText: 'Cancel',
        tone: 'danger',
      });
      if (!ok) return;
      socket.emit('fleet:kick', { memberId });
    })();
  };

  const handlePromoteMember = (memberId: string, currentRank: FleetRank): void => {
    if (!socket) return;
    const rankOrder: FleetRank[] = ['member', 'officer', 'commander', 'vice_admiral'];
    const currentIndex = rankOrder.indexOf(currentRank);
    if (currentIndex < rankOrder.length - 1) {
      const newRank = rankOrder[currentIndex + 1];
      socket.emit('fleet:promote', { memberId, newRank });
    }
  };

  const handleDemoteMember = (memberId: string, currentRank: FleetRank): void => {
    if (!socket) return;
    const rankOrder: FleetRank[] = ['member', 'officer', 'commander', 'vice_admiral'];
    const currentIndex = rankOrder.indexOf(currentRank);
    if (currentIndex > 0) {
      const newRank = rankOrder[currentIndex - 1];
      socket.emit('fleet:demote', { memberId, newRank });
    }
  };

  if (!isOpen) return null;

  // ============ RENDER HELPERS ============

  const renderNoFleet = (): React.ReactNode => {
    if (showCreateForm) {
      return renderCreateForm();
    }

    const canCreate = playerLevel >= FLEET_CREATION_MIN_LEVEL && playerBerries >= FLEET_CREATION_COST;

    return (
      <div style={{ padding: '16px' }}>
        <div style={{ backgroundColor: '#161b22', border: '1px solid #30363d', padding: '16px', textAlign: 'center' }}>
          <div style={{ color: '#8b949e', fontSize: '11px', letterSpacing: 1, marginBottom: '8px' }}>
            FLEET STATUS
          </div>
          <div style={{ color: '#e6edf3', fontSize: '18px', fontWeight: 800, marginBottom: '10px' }}>
            YOU ARE NOT IN A FLEET
          </div>
          <div style={{ color: '#8b949e', fontSize: '12px', lineHeight: 1.6, maxWidth: 520, margin: '0 auto 16px auto' }}>
            Fleets are player organizations that can claim territory, share resources, and work together.
          </div>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              type="button"
              style={{
                ...buttonStyle('primary'),
                opacity: canCreate ? 1 : 0.5,
                cursor: canCreate ? 'pointer' : 'not-allowed',
              }}
              onClick={() => canCreate && setShowCreateForm(true)}
              disabled={!canCreate}
            >
              CREATE FLEET (₱{FLEET_CREATION_COST.toLocaleString()})
            </button>
          </div>

          {!canCreate && (
            <div style={{ color: '#f85149', fontSize: '11px', marginTop: '10px' }}>
              {playerLevel < FLEET_CREATION_MIN_LEVEL
                ? `Requires level ${FLEET_CREATION_MIN_LEVEL} (you are level ${playerLevel})`
                : `Requires ₱${FLEET_CREATION_COST.toLocaleString()}`}
            </div>
          )}
        </div>

        {invitations.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ color: '#8b949e', fontSize: '11px', letterSpacing: 1, marginBottom: '8px' }}>
              PENDING INVITATIONS
            </div>
            {invitations.map(inv => (
              <div key={inv.id} style={{
                backgroundColor: '#161b22',
                border: '1px solid #30363d',
                padding: '12px',
                marginBottom: '10px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <div>
                  <span style={{ color: '#e6edf3' }}>
                    {inv.fleetTag ? `[${inv.fleetTag}] ` : ''}{inv.fleetName}
                  </span>
                  <span style={{ color: '#8b949e', marginLeft: '8px', fontSize: '11px' }}>
                    from {inv.inviterName}
                  </span>
                </div>
                <div>
                  <button
                    style={{ ...buttonStyle('primary'), marginRight: '8px' }}
                    onClick={() => handleRespondToInvite(inv.id, true)}
                  >
                    ACCEPT
                  </button>
                  <button
                    style={buttonStyle('danger')}
                    onClick={() => handleRespondToInvite(inv.id, false)}
                  >
                    DECLINE
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderCreateForm = (): React.ReactNode => (
    <div style={{ padding: '20px' }}>
      <h2 style={{ color: '#ffffff', marginBottom: '20px' }}>Create Fleet</h2>
      
      <div style={{ marginBottom: '16px' }}>
        <label style={{ color: '#8b949e', display: 'block', marginBottom: '4px' }}>Fleet Name *</label>
        <input
          type="text"
          style={inputStyle}
          value={createName}
          onChange={(e) => setCreateName(e.target.value)}
          placeholder="Enter fleet name (3-24 characters)"
          maxLength={24}
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ color: '#8b949e', display: 'block', marginBottom: '4px' }}>Tag (optional)</label>
        <input
          type="text"
          style={{ ...inputStyle, width: '100px' }}
          value={createTag}
          onChange={(e) => setCreateTag(e.target.value.toUpperCase())}
          placeholder="TAG"
          maxLength={5}
        />
        <span style={{ color: '#8b949e', marginLeft: '8px', fontSize: '11px' }}>
          2-5 characters, shown as [TAG]
        </span>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ color: '#8b949e', display: 'block', marginBottom: '4px' }}>Description (optional)</label>
        <textarea
          style={{ ...inputStyle, height: '60px', resize: 'none' }}
          value={createDescription}
          onChange={(e) => setCreateDescription(e.target.value)}
          placeholder="Describe your fleet..."
          maxLength={500}
        />
      </div>

      <div style={{ marginBottom: '24px' }}>
        <label style={{ color: '#8b949e', display: 'block', marginBottom: '8px' }}>Join Policy</label>
        <div style={{ display: 'flex', gap: '16px' }}>
          <label style={{ color: '#ffffff', cursor: 'pointer' }}>
            <input
              type="radio"
              checked={createJoinPolicy === 'invite_only'}
              onChange={() => setCreateJoinPolicy('invite_only')}
              style={{ marginRight: '8px' }}
            />
            Invite Only
          </label>
          <label style={{ color: '#ffffff', cursor: 'pointer' }}>
            <input
              type="radio"
              checked={createJoinPolicy === 'open'}
              onChange={() => setCreateJoinPolicy('open')}
              style={{ marginRight: '8px' }}
            />
            Open (anyone can apply)
          </label>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px' }}>
        <button style={buttonStyle('primary')} onClick={handleCreateFleet}>
          Create Fleet (₱{FLEET_CREATION_COST.toLocaleString()})
        </button>
        <button style={buttonStyle('secondary')} onClick={() => setShowCreateForm(false)}>
          Cancel
        </button>
      </div>
    </div>
  );

  const renderOverview = (): React.ReactNode => {
    if (!fleet) return null;

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <h2 style={{ color: '#ffffff', margin: 0 }}>
              {fleet.tag ? `[${fleet.tag}] ` : ''}{fleet.name}
            </h2>
            <p style={{ color: '#8b949e', margin: '4px 0' }}>
              Founded by {fleet.founderName}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: RANK_DISPLAY[myRank || 'member'].color, fontWeight: 'bold' }}>
              {RANK_DISPLAY[myRank || 'member'].label}
            </div>
            <div style={{ color: '#8b949e', fontSize: '11px' }}>
              {fleet.joinPolicy === 'open' ? 'Open' : 'Invite Only'}
            </div>
          </div>
        </div>

        {fleet.motd && (
          <div style={{
            backgroundColor: '#2d3748',
            padding: '12px',
            marginBottom: '16px',
            borderLeft: '3px solid #f39c12',
          }}>
            <div style={{ color: '#f39c12', fontSize: '11px', marginBottom: '4px' }}>MESSAGE OF THE DAY</div>
            <div style={{ color: '#ffffff' }}>{fleet.motd}</div>
          </div>
        )}

        {fleet.description && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ color: '#8b949e', fontSize: '11px', marginBottom: '4px' }}>DESCRIPTION</div>
            <div style={{ color: '#ffffff' }}>{fleet.description}</div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
          <div style={{ backgroundColor: '#2d3748', padding: '12px', textAlign: 'center' }}>
            <div style={{ color: '#3498db', fontSize: '20px', fontWeight: 'bold' }}>
              {fleet.memberCount}/{fleet.maxMembers}
            </div>
            <div style={{ color: '#8b949e', fontSize: '11px' }}>MEMBERS</div>
          </div>
          <div style={{ backgroundColor: '#2d3748', padding: '12px', textAlign: 'center' }}>
            <div style={{ color: '#f39c12', fontSize: '20px', fontWeight: 'bold' }}>
              ₱{fleet.treasury.toLocaleString()}
            </div>
            <div style={{ color: '#8b949e', fontSize: '11px' }}>TREASURY</div>
          </div>
          <div style={{ backgroundColor: '#2d3748', padding: '12px', textAlign: 'center' }}>
            <div style={{ color: '#2ecc71', fontSize: '20px', fontWeight: 'bold' }}>
              {fleet.territoryCount}/{fleet.maxTerritories}
            </div>
            <div style={{ color: '#8b949e', fontSize: '11px' }}>TERRITORIES</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          {myRank !== 'admiral' && (
            <button style={buttonStyle('danger')} onClick={handleLeaveFleet}>
              Leave Fleet
            </button>
          )}
          {myRank === 'admiral' && (
            <button style={buttonStyle('danger')} onClick={handleDisbandFleet}>
              Disband Fleet
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderMembers = (): React.ReactNode => {
    if (!fleet) return null;

    const myRankLevel = myRank ? ['member', 'officer', 'commander', 'vice_admiral', 'admiral'].indexOf(myRank) : 0;

    return (
      <div>
        <h3 style={{ color: '#ffffff', marginBottom: '16px' }}>
          Members ({members.length}/{fleet.maxMembers})
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {members.map(member => {
            const memberRankLevel = ['member', 'officer', 'commander', 'vice_admiral', 'admiral'].indexOf(member.rank);
            const canManage = myRankLevel > memberRankLevel && member.characterId !== characterId;
            const memberDisplayName: string = fleet.tag
              ? formatDisplayName({ baseName: member.characterName, fleetTag: fleet.tag })
              : member.characterName;

            return (
              <div
                key={member.id}
                style={{
                  backgroundColor: '#2d3748',
                  padding: '12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <span style={{ color: RANK_DISPLAY[member.rank].color, marginRight: '8px' }}>
                    [{RANK_DISPLAY[member.rank].abbrev}]
                  </span>
                  <span style={{ color: '#ffffff' }}>{memberDisplayName}</span>
                  {member.characterId === characterId && (
                    <span style={{ color: '#2ecc71', marginLeft: '8px' }}>(You)</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {canManage && member.rank !== 'admiral' && memberRankLevel < 3 && (
                    <button
                      style={{ ...buttonStyle('secondary'), padding: '4px 8px', fontSize: '10px' }}
                      onClick={() => handlePromoteMember(member.id, member.rank)}
                    >
                      Promote
                    </button>
                  )}
                  {canManage && member.rank !== 'member' && (
                    <button
                      style={{ ...buttonStyle('secondary'), padding: '4px 8px', fontSize: '10px' }}
                      onClick={() => handleDemoteMember(member.id, member.rank)}
                    >
                      Demote
                    </button>
                  )}
                  {canManage && (
                    <button
                      style={{ ...buttonStyle('danger'), padding: '4px 8px', fontSize: '10px' }}
                      onClick={() => handleKickMember(member.id, member.characterName)}
                    >
                      Kick
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTreasury = (): React.ReactNode => {
    if (!fleet) return null;

    const canWithdraw = myRank === 'admiral' || myRank === 'vice_admiral';

    // Check if player is at a Harbor Office
    if (!isDockedAtHarborOffice) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ color: '#f39c12', fontSize: '24px', marginBottom: '16px' }}>[HARBOR OFFICE REQUIRED]</div>
          <p style={{ color: '#8b949e', marginBottom: '16px' }}>
            You must dock at a Harbor Office to access fleet treasury and upgrades.
          </p>
          <p style={{ color: '#8b949e', fontSize: '11px' }}>
            Harbor Offices are located throughout the world. Look for them on your map.
          </p>
          <div style={{ marginTop: '24px', backgroundColor: '#2d3748', padding: '16px' }}>
            <div style={{ color: '#8b949e', fontSize: '11px', marginBottom: '8px' }}>CURRENT TREASURY BALANCE</div>
            <div style={{ color: '#f39c12', fontSize: '20px', fontWeight: 'bold' }}>
              ₱{fleet.treasury.toLocaleString()}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div style={{ backgroundColor: '#16213e', padding: '8px 12px', marginBottom: '16px', borderLeft: '3px solid #2ecc71' }}>
          <span style={{ color: '#2ecc71', fontSize: '11px' }}>DOCKED AT: </span>
          <span style={{ color: '#ffffff', fontSize: '11px' }}>{harborOfficeName || 'Harbor Office'}</span>
        </div>
        <div style={{ backgroundColor: '#2d3748', padding: '20px', marginBottom: '20px', textAlign: 'center' }}>
          <div style={{ color: '#f39c12', fontSize: '28px', fontWeight: 'bold' }}>
            ₱{fleet.treasury.toLocaleString()}
          </div>
          <div style={{ color: '#8b949e', fontSize: '12px' }}>
            Capacity: ₱{fleet.treasuryCapacity.toLocaleString()}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
          <div>
            <h4 style={{ color: '#ffffff', marginBottom: '8px' }}>Deposit</h4>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="number"
                style={{ ...inputStyle, flex: 1 }}
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                placeholder="Amount"
                min={1}
              />
              <button style={buttonStyle('primary')} onClick={handleDeposit}>
                Deposit
              </button>
            </div>
            <div style={{ color: '#8b949e', fontSize: '11px', marginTop: '4px' }}>
              Your balance: ₱{playerBerries.toLocaleString()}
            </div>
          </div>

          <div>
            <h4 style={{ color: '#ffffff', marginBottom: '8px' }}>Withdraw</h4>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="number"
                style={{ ...inputStyle, flex: 1, opacity: canWithdraw ? 1 : 0.5 }}
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder="Amount"
                min={1}
                disabled={!canWithdraw}
              />
              <button
                style={{ ...buttonStyle('primary'), opacity: canWithdraw ? 1 : 0.5 }}
                onClick={handleWithdraw}
                disabled={!canWithdraw}
              >
                Withdraw
              </button>
            </div>
            {!canWithdraw && (
              <div style={{ color: '#e74c3c', fontSize: '11px', marginTop: '4px' }}>
                Vice Admiral+ required
              </div>
            )}
          </div>
        </div>

        <h4 style={{ color: '#ffffff', marginBottom: '12px' }}>Upgrades</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {(Object.keys(UPGRADE_DISPLAY) as FleetUpgradeType[]).map(upgradeType => {
            const upgrade = upgrades.find(u => u.upgradeType === upgradeType);
            const currentLevel = upgrade?.level || 0;
            const nextCost = currentLevel < 5 ? UPGRADE_COSTS[upgradeType][currentLevel] : null;
            const canPurchase = (myRank === 'admiral' || myRank === 'vice_admiral') && nextCost !== null && fleet.treasury >= nextCost;

            return (
              <div
                key={upgradeType}
                style={{
                  backgroundColor: '#2d3748',
                  padding: '12px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ color: '#ffffff' }}>
                    {UPGRADE_DISPLAY[upgradeType].icon} {UPGRADE_DISPLAY[upgradeType].label}
                  </span>
                  <span style={{ color: '#3498db' }}>Lv.{currentLevel}/5</span>
                </div>
                <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                  {UPGRADE_DISPLAY[upgradeType].description}
                </div>
                {nextCost !== null && (
                  <button
                    style={{
                      ...buttonStyle('secondary'),
                      width: '100%',
                      padding: '6px',
                      fontSize: '10px',
                      opacity: canPurchase ? 1 : 0.5,
                    }}
                    onClick={() => canPurchase && handlePurchaseUpgrade(upgradeType)}
                    disabled={!canPurchase}
                  >
                    Upgrade (₱{nextCost.toLocaleString()})
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderActivity = (): React.ReactNode => {
    if (!fleet) return null;

    const formatActivity = (activity: FleetActivityLog): string => {
      switch (activity.activityType) {
        case 'member_joined':
          return `${activity.actorName} joined the fleet`;
        case 'member_left':
          return `${activity.actorName} left the fleet`;
        case 'member_kicked':
          return `${activity.actorName} kicked ${activity.targetName}`;
        case 'member_promoted':
          return `${activity.actorName} promoted ${activity.targetName}`;
        case 'member_demoted':
          return `${activity.actorName} demoted ${activity.targetName}`;
        case 'treasury_deposit':
          return `${activity.actorName} deposited ${(activity.details.amount as number)?.toLocaleString() || '?'} pieces`;
        case 'treasury_withdraw':
          return `${activity.actorName} withdrew ${(activity.details.amount as number)?.toLocaleString() || '?'} pieces`;
        case 'upgrade_purchased':
          return `${activity.actorName} purchased ${activity.details.upgradeType} upgrade`;
        case 'fleet_created':
          return `Fleet created`;
        case 'leadership_transferred':
          return `Leadership transferred to ${activity.targetName}`;
        default:
          return activity.activityType.replace(/_/g, ' ');
      }
    };

    return (
      <div>
        <h3 style={{ color: '#ffffff', marginBottom: '16px' }}>Activity Log</h3>
        {activities.length === 0 ? (
          <p style={{ color: '#8b949e' }}>No recent activity</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {activities.map(activity => (
              <div
                key={activity.id}
                style={{
                  backgroundColor: '#2d3748',
                  padding: '10px 12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <span style={{ color: '#ffffff' }}>{formatActivity(activity)}</span>
                <span style={{ color: '#8b949e', fontSize: '11px' }}>
                  {new Date(activity.createdAt).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderSettings = (): React.ReactNode => {
    if (!fleet) return null;

    const canManageApplications = myRank === 'admiral' || myRank === 'vice_admiral' || myRank === 'commander';

    return (
      <div>
        {fleet.joinPolicy === 'open' && canManageApplications && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ color: '#ffffff', marginBottom: '12px' }}>
              Pending Applications ({applications.length})
            </h3>
            {applications.length === 0 ? (
              <p style={{ color: '#8b949e' }}>No pending applications</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {applications.map(app => (
                  <div
                    key={app.id}
                    style={{
                      backgroundColor: '#2d3748',
                      padding: '12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <span style={{ color: '#ffffff' }}>{app.applicantName}</span>
                      <span style={{ color: '#8b949e', marginLeft: '8px' }}>Lv.{app.applicantLevel}</span>
                      {app.message && (
                        <div style={{ color: '#8b949e', fontSize: '11px', marginTop: '4px' }}>
                          "{app.message}"
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        style={buttonStyle('primary')}
                        onClick={() => handleReviewApplication(app.id, true)}
                      >
                        Accept
                      </button>
                      <button
                        style={buttonStyle('danger')}
                        onClick={() => handleReviewApplication(app.id, false)}
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <h3 style={{ color: '#ffffff', marginBottom: '12px' }}>Fleet Info</h3>
        <div style={{ backgroundColor: '#2d3748', padding: '12px' }}>
          <div style={{ marginBottom: '8px' }}>
            <span style={{ color: '#8b949e' }}>Created: </span>
            <span style={{ color: '#ffffff' }}>{new Date(fleet.createdAt).toLocaleDateString()}</span>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <span style={{ color: '#8b949e' }}>Join Policy: </span>
            <span style={{ color: '#ffffff' }}>{fleet.joinPolicy === 'open' ? 'Open' : 'Invite Only'}</span>
          </div>
          <div>
            <span style={{ color: '#8b949e' }}>Reputation: </span>
            <span style={{ color: '#ffffff' }}>{fleet.reputation}</span>
          </div>
        </div>
      </div>
    );
  };

  // ============ MAIN RENDER ============

  return (
    <>
      <div className="panel-overlay" style={{ zIndex: 5000 }} onClick={onClose}>
        <div style={{ ...panelStyle, zIndex: 5001 }} onClick={(e) => e.stopPropagation()}>
          <div style={headerStyle}>
            <h2 style={{ color: '#e6edf3', margin: 0, fontSize: '16px', fontWeight: 800, letterSpacing: 1 }}>
              {fleet ? (fleet.tag ? `[${fleet.tag}] ${fleet.name}` : fleet.name) : 'FLEET'}
            </h2>
            <button className="close-btn" onClick={onClose} type="button">×</button>
          </div>

          {message && (
            <div style={{
              padding: '8px 16px',
              backgroundColor: '#161b22',
              borderBottom: `1px solid ${message.type === 'success' ? '#2ea043' : '#f85149'}`,
              color: message.type === 'success' ? '#2ea043' : '#f85149',
              fontSize: '12px',
              textAlign: 'center',
              fontWeight: 700,
            }}>
              {message.text}
            </div>
          )}

          {isLoading ? (
            <div style={{ padding: '24px', textAlign: 'center', color: '#8b949e' }}>
              LOADING...
            </div>
          ) : !fleet ? (
            <div style={{ padding: 0 }}>{renderNoFleet()}</div>
          ) : (
            <>
              <div style={tabsStyle}>
                {(['overview', 'members', 'treasury', 'activity', 'settings'] as TabType[]).map(tab => (
                  <button
                    key={tab}
                    style={tabStyle(activeTab === tab)}
                    onClick={() => setActiveTab(tab)}
                    type="button"
                  >
                    {tab.toUpperCase()}
                  </button>
                ))}
              </div>
              <div style={contentStyle}>
                {activeTab === 'overview' && renderOverview()}
                {activeTab === 'members' && renderMembers()}
                {activeTab === 'treasury' && renderTreasury()}
                {activeTab === 'activity' && renderActivity()}
                {activeTab === 'settings' && renderSettings()}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
